import React, { useState } from "react";
import { api, extractError } from "@/lib/api";
import { X } from "lucide-react";

export default function NewClientModal({ categories, collaborators, onClose, onCreated, initial = null }) {
    const [form, setForm] = useState(initial || {
        first_name: "", last_name: "", role: "", company: "",
        email: "", phone: "", birthday: "",
        lead_ranking: "tiepido", category_id: "", collaborator_id: "",
        base_notes: "",
    });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            const payload = { ...form };
            if (!payload.category_id) payload.category_id = null;
            if (!payload.collaborator_id) payload.collaborator_id = null;
            if (!payload.birthday) payload.birthday = null;
            if (initial?.id) {
                await api.patch(`/clients/${initial.id}`, payload);
            } else {
                await api.post("/clients", payload);
            }
            onCreated();
        } catch (e) {
            setErr(extractError(e));
        } finally { setBusy(false); }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                data-testid="new-client-modal"
                className="bg-white rounded-md shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
                <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                    <div className="font-display font-semibold">{initial?.id ? "Modifica cliente" : "Nuovo cliente"}</div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3 text-sm">
                    <Field label="Nome*"><input required data-testid="client-firstname" value={form.first_name} onChange={set("first_name")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></Field>
                    <Field label="Cognome"><input value={form.last_name} onChange={set("last_name")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></Field>
                    <Field label="Ruolo aziendale"><input value={form.role || ""} onChange={set("role")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></Field>
                    <Field label="Azienda"><input value={form.company || ""} onChange={set("company")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></Field>
                    <Field label="Email"><input type="email" value={form.email || ""} onChange={set("email")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></Field>
                    <Field label="Telefono"><input value={form.phone || ""} onChange={set("phone")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></Field>
                    <Field label="Data di nascita (gg/mm/aaaa)"><input type="date" value={form.birthday || ""} onChange={set("birthday")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" /></Field>
                    <Field label="Lead ranking">
                        <select value={form.lead_ranking} onChange={set("lead_ranking")} className="w-full px-2 py-1.5 border border-neutral-300 rounded">
                            <option value="caldo">Caldo</option>
                            <option value="tiepido">Tiepido</option>
                            <option value="freddo">Freddo</option>
                        </select>
                    </Field>
                    <Field label="Categoria">
                        <select data-testid="client-category" value={form.category_id || ""} onChange={set("category_id")} className="w-full px-2 py-1.5 border border-neutral-300 rounded">
                            <option value="">— Nessuna —</option>
                            {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                        </select>
                    </Field>
                    <Field label="Procurato da">
                        <select value={form.collaborator_id || ""} onChange={set("collaborator_id")} className="w-full px-2 py-1.5 border border-neutral-300 rounded">
                            <option value="">— Nessuno —</option>
                            {collaborators.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                        </select>
                    </Field>
                    <Field label="Note base" className="col-span-2">
                        <textarea rows={3} value={form.base_notes || ""} onChange={set("base_notes")} className="w-full px-2 py-1.5 border border-neutral-300 rounded" />
                    </Field>
                </div>
                {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
                <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                    <button type="submit" disabled={busy} data-testid="save-client-btn" className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                        {busy ? "Salvataggio…" : "Salva"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="text-xs font-medium text-neutral-600">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
